/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1763337600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 17 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "qualified",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Qualified",
          "depth": 0,
          "id": "connect-to-qualified"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Object selection",
          "depth": 1,
          "id": "object-selection"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Sync performance",
          "depth": 1,
          "id": "sync-performance"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      code: "code",
      h3: "h3",
      ul: "ul",
      li: "li",
      h4: "h4"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Qualified is a conversational sales and marketing platform that helps revenue teams identify and engage with website visitors. With the Qualified destination, you can sync lead and company data from your data warehouse to Qualified, ensuring your sales team has the most up-to-date information when engaging with prospects."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Leads"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to leads in Qualified"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.qualified.com/api",
                children: "Qualified API docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Companies"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to companies in Qualified"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://www.qualified.com/api",
                children: "Qualified API docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-qualified",
        children: "Connect to Qualified"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Qualified"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You will be required to enter your Qualified API key. Generate a Qualified API key with access to the Enterprise bulk endpoints in your Qualified account. The API key must have permissions to access the bulk API endpoints for leads and companies."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The API key must have ", _jsx(_components.code, {
            children: "Enterprise bulk access"
          }), " to use the Qualified destination. Confirm that your API key has the necessary permissions before connecting."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Paste the API key exactly as provided; Hightouch passes it as a Bearer token in API requests."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Qualified destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Qualified destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "object-selection",
        children: "Object selection"
      }), "\n", _jsx(_components.p, {
        children: "Choose which Qualified object to sync to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Leads"
          }), ": Sync lead data to Qualified"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Companies"
          }), ": Sync company data to Qualified"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records are matched from your model to Qualified based on the object type:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Leads"
          }), ": Match by ", _jsx(_components.strong, {
            children: "Email"
          }), ". The email address is the primary identifier for leads in Qualified. Qualified upserts leads by email address."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Companies"
          }), ": Match by ", _jsx(_components.strong, {
            children: "Domain"
          }), ". The company domain is the primary identifier for companies in Qualified. Qualified upserts companies by domain."]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The record should be matched using a unique identifier. For leads, ensure the email column contains unique values. For companies, ensure the domain column contains unique values. Duplicate identifiers may cause sync errors or unexpected behavior."
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Leads synced to Qualified will not appear in the Qualified interface until they have activity associated with them (such as website visits or engagement). The data is successfully synced, but Qualified requires activity before displaying leads in the platform."
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Qualified's default and custom fields. Map Hightouch columns to Qualified fields in the field mapping section."
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing ", _jsx(_components.strong, {
          children: "Leads"
        }), ", you can map both:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Lead fields"
          }), ": Fields that belong to the lead record"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Company fields"
          }), ": Fields that belong to the associated company record"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When syncing ", _jsx(_components.strong, {
          children: "Companies"
        }), ", you can map:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Company fields"
          }), ": Fields that belong to the company record"]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Qualified requires object fields to be created in advance. After adding new fields in Qualified, click \"Refresh fields\" in the mapper to pull the latest definitions. Hightouch cannot provision fields automatically."
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "sync-performance",
        children: "Sync performance"
      }), "\n", _jsx(_components.p, {
        children: "Qualified uses bulk API endpoints to efficiently sync large volumes of data. The destination automatically:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Groups rows into bulk jobs (up to 500 rows per job)"
        }), "\n", _jsx(_components.li, {
          children: "Manages job polling and status checks"
        }), "\n", _jsx(_components.li, {
          children: "Handles rate limiting with automatic retries"
        }), "\n", _jsx(_components.li, {
          children: "Processes job results and reports rejected rows"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you experience rate limiting, contact Qualified support to understand your account's rate limits."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "api-key-authentication-errors",
        children: "API key authentication errors"
      }), "\n", _jsx(_components.p, {
        children: "If you receive authentication errors, verify that:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Your API key is correct and has not expired"
        }), "\n", _jsx(_components.li, {
          children: "The API key has Enterprise bulk access permissions"
        }), "\n", _jsx(_components.li, {
          children: "The API key has access to both lead and company endpoints"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "rate-limiting",
        children: "Rate limiting"
      }), "\n", _jsx(_components.p, {
        children: "If you encounter rate limit errors (429 or 503 status codes), the destination will automatically retry with exponential backoff. Contact Qualified support to understand your account's specific rate limits."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-errors",
        children: "Field mapping errors"
      }), "\n", _jsx(_components.p, {
        children: "If you receive errors about invalid fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Ensure custom fields exist in Qualified before mapping to them"
        }), "\n", _jsx(_components.li, {
          children: "Verify field names match exactly (field names are case-sensitive)"
        }), "\n", _jsx(_components.li, {
          children: "Check that the data types in your model match the expected types in Qualified"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
